import { RichTextContent } from "../../../content/components";
import { StaticSiteDataContext } from "../../../contexts/StaticSiteDataContext";
import { isEmptySlateDocument } from "../../../core/constants";
import {
  AttendantDetails,
  Configuration,
  Page,
  Section,
} from "../../../resources/modelTypes";
import SitePreviewContext from "../../../website/components/SitePreviewContext";
import { selectors } from "../../cypressSelectors";
import { DisplayPhoto } from "../DisplayPhoto";
import { css } from "@emotion/react";
import { SmallHeadline, TinyHeadline } from "@minted/fancyclothes";
import { theme } from "@minted/minted-components";
import React, { memo, useContext } from "react";

/**
 * Custom hook to handle section edit clicks using context
 */
const useEditClick = (page: Page) => {
  const { onSectionEditClick } = useContext(SitePreviewContext);
  const { isOnlineInvitationSite } = useContext(StaticSiteDataContext);

  return (sectionId: number) => {
    if (onSectionEditClick && isOnlineInvitationSite) {
      onSectionEditClick(sectionId, "section", page);
    }
  };
};

export type AttendantSection = Section & {
  sectionDetails: AttendantDetails;
};

type DisplayAttendantSectionProps = {
  configuration: Configuration;
  page: Page;
  sections: AttendantSection[];
};

type SingleAttendantProps = {
  configuration: Configuration;
  page: Page;
  section: AttendantSection;
};

const singleAttendantStyles = {
  spacing: css`
    margin-bottom: ${theme.spacing.x10};
  `,
};

const SingleAttendant = ({
  configuration,
  page,
  section,
}: SingleAttendantProps) => {
  const handleEditClick = useEditClick(page);

  return (
    <div
      css={singleAttendantStyles.spacing}
      data-cy={`${selectors.website_displaySection}_attendant`}
      id={`section-${section.id || "new"}`}
      key={`attendantGroup${section.id}`}
      onClick={() => handleEditClick(section.id)}
    >
      {(section.photo || section.image) && (
        <DisplayPhoto configuration={configuration} section={section} />
      )}
      <div data-cy="subheading">
        <SmallHeadline>{section.sectionDetails.name}</SmallHeadline>
      </div>
      <div data-cy="body">
        <TinyHeadline>{section.sectionDetails.title}</TinyHeadline>
        {!isEmptySlateDocument(section.sectionDetails.content) && (
          <div>
            <RichTextContent value={section.sectionDetails.content} />
          </div>
        )}
      </div>
    </div>
  );
};

export type GroupedAttendantsProps = {
  configuration: Configuration;
  page: Page;
  sections: AttendantSection[];
};

const groupedAttendantsStyles = {
  grid: css`
    display: grid;
    grid-template-columns: repeat(
      auto-fit,
      minmax(250px, 1fr)
    ); // Display 2 columns unless single attendant
    gap: ${theme.spacing.x4};
    justify-content: center;
    justify-items: center;
  `,
};

const GroupedAttendants = ({
  configuration,
  page,
  sections,
}: GroupedAttendantsProps) => {
  const handleEditClick = useEditClick(page);

  return (
    <div
      css={groupedAttendantsStyles.grid}
      data-cy={`${selectors.website_displaySection}_attendantGroup`}
      id={`section-${sections[0].id || "new"}`}
      key={`attendantGroup${sections[0].id}`}
    >
      {sections.map((section) => (
        <div key={section.id} onClick={() => handleEditClick(section.id)}>
          <SingleAttendant
            configuration={configuration}
            page={page}
            section={section}
          />
        </div>
      ))}
    </div>
  );
};

const DisplayAttendantSections = ({
  configuration,
  page,
  sections,
}: DisplayAttendantSectionProps) => {
  const { isMobile } = useContext(SitePreviewContext);

  if (configuration.pageLayout === "multi" && !isMobile) {
    return (
      <GroupedAttendants
        configuration={configuration}
        page={page}
        sections={sections}
      />
    );
  }

  return (
    <>
      {sections.map((section) => (
        <SingleAttendant
          configuration={configuration}
          key={section.id}
          page={page}
          section={section}
        />
      ))}
    </>
  );
};

const MemoizedDisplayAttendantSection = memo(DisplayAttendantSections);

export { MemoizedDisplayAttendantSection as DisplayAttendantSections };
